import { zodResolver } from "@hookform/resolvers/zod";
import { Button, ButtonGroup, Divider, Grid, Paper, Typography } from "@mui/material";
import { useEffect } from "react";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import Address from "@/components/common/Address";
import type { OrganizationFormSchemaType } from "@/schemas/organization-form";
import {
  OrganizationFormSchema,
  OrganizationFormSchemaDefaultValues,
} from "@/schemas/organization-form";
import type { MemberFormSchemaType } from "@/schemas/organization-form/member.schema";

import { MemberFormUI } from "../../Member/Form";
import EnterpriseName from "../EnterpriseName";

type TForm = {
  onSubmit: SubmitHandler<OrganizationFormSchemaType>;
  values?: OrganizationFormSchemaType;
  disabledFields?: Array<keyof MemberFormSchemaType>;
};

function Form({ onSubmit, values, disabledFields }: TForm) {
  const { t } = useTranslation();
  const methods = useForm<
    z.input<typeof OrganizationFormSchema>,
    unknown,
    OrganizationFormSchemaType
  >({
    mode: "onChange",
    resolver: zodResolver(OrganizationFormSchema),
    values: values ?? OrganizationFormSchemaDefaultValues,
  });

  useEffect(() => {
    if (values) {
      methods.reset(values);
    }
  }, [values, methods]);
  return (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          void methods.handleSubmit(onSubmit)(e);
        }}
        spacing={4}
      >
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={4}
            sx={{
              backgroundColor: "#E7E6F7",
              borderRadius: 1,
              padding: "20px",
              height: "100%",
            }}
          >
            <EnterpriseName />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={4}
            sx={{
              backgroundColor: "#E7E6F7",
              borderRadius: 1,
              padding: "20px",
              height: "100%",
            }}
          >
            <Address title="Adresse" identifier="owner.address" />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper
            elevation={4}
            sx={{
              backgroundColor: "#E7E6F7",
              borderRadius: 1,
              padding: "20px",
              height: "100%",
            }}
          >
            <Grid container spacing={2} paddingBottom={2}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="h5">Informations du responsable</Typography>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ bgcolor: "#fe5d8d", marginBottom: 3 }} />
              </Grid>
              <MemberFormUI disabledFields={disabledFields} t={useTranslation().t} name="owner" />
            </Grid>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ButtonGroup fullWidth size="large" sx={{ gap: 5 }}>
            <Button variant="outlined" color="warning" onClick={() => methods.reset()}>
              {t("common.actions.cancel")}
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {t("common.actions.submit")}
            </Button>
          </ButtonGroup>
        </Grid>
      </Grid>
    </FormProvider>
  );
}

export default Form;
